import { type FC, type ReactNode, Suspense, useCallback, useEffect, useMemo } from 'react';
import { graphql, usePreloadedQuery, useQueryLoader, type PreloadedQuery } from 'react-relay';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';

import { UserRecommendationsGridLayout } from './UserRecommendationsGridLayout.tsx';
import { type UserRecommendationsTitleAlign } from './UserRecommendationsTitle.tsx';
import { UserRecommendationsRecommendationTile } from './UserRecommendationsRecommendationTile.tsx';
import { useUserRecommendationsGridExpand } from './useUserRecommendationsGridExpand.ts';
import { useUserRecommendationsItemCounts } from './useUserRecommendationsItemCounts.ts';
import { getUserRecommendationsItemsPerRow } from './userRecommendationsGridConstants.ts';
import { renderUserRecommendationsGridShimmers } from './userRecommendationsGridShimmers.tsx';

import { type UserRecommendationsGridQuery } from './__generated__/UserRecommendationsGridQuery.graphql.ts';

export const userRecommendationsGridQuery = graphql`
    query UserRecommendationsGridQuery(
        $itemIds: [String]!
        $userId: String!
        $isTrade: Boolean!
        $priceBookName: String!
    ) {
        viewer {
            ...UserRecommendationsRecommendationTile_viewer
                @arguments(fetchFavorites: true, userId: $userId)
            itemRead(itemIds: $itemIds) {
                ... on Item {
                    __typename
                    serviceId
                    ...UserRecommendationsRecommendationTile_item
                        @arguments(
                            fetchFavorites: true
                            userId: $userId
                            isTrade: $isTrade
                            priceBookName: $priceBookName
                        )
                }
            }
            user(userId: $userId) {
                ...UserRecommendationsRecommendationTile_user
            }
        }
    }
`;

type UserRecommendationsLoadedRowsProps = {
    queryRef: PreloadedQuery<UserRecommendationsGridQuery>;
    itemsPerRow: number;
    isMobile: boolean;
    trackingTrigger: string;
    dataTn?: string;
    startIndex: number;
    currency: string;
};

const UserRecommendationsLoadedRows: FC<UserRecommendationsLoadedRowsProps> = ({
    queryRef,
    itemsPerRow,
    isMobile,
    trackingTrigger,
    dataTn,
    startIndex,
    currency,
}) => {
    const data = usePreloadedQuery<UserRecommendationsGridQuery>(
        userRecommendationsGridQuery,
        queryRef
    );
    const items =
        data.viewer?.itemRead?.filter(filterFalsy).filter(item => item.__typename === 'Item') || [];

    if (!items.length) {
        return null;
    }

    return (
        <>
            {items.map((item, index) => (
                <UserRecommendationsRecommendationTile
                    key={item.serviceId}
                    item={item}
                    viewer={data.viewer}
                    user={data.viewer?.user}
                    index={startIndex + index}
                    itemsPerRow={itemsPerRow}
                    isMobile={isMobile}
                    trackingTrigger={trackingTrigger}
                    dataTn={dataTn}
                    currency={currency}
                />
            ))}
        </>
    );
};

export type UserRecommendationsItemsGridProps = {
    itemIds: readonly string[];
    userId: string;
    trackingTrigger: string;
    isMobile: boolean;
    isTablet: boolean;
    isTrade: boolean;
    priceBookName: string;
    dataTn?: string;
    title?: ReactNode;
    currency: string;
    titleAlign?: UserRecommendationsTitleAlign;
    autoLoadMore?: boolean;
};

const UserRecommendationsItemsGrid: FC<UserRecommendationsItemsGridProps> = ({
    itemIds: itemIdsProp,
    userId,
    trackingTrigger,
    isMobile,
    isTablet,
    isTrade,
    priceBookName,
    dataTn = 'user-recommendations',
    title,
    currency,
    titleAlign = 'center',
    autoLoadMore = false,
}) => {
    const { initialItemCount, maxItemCount, moreItemCount } =
        useUserRecommendationsItemCounts(isMobile);
    const itemIds = useMemo(() => itemIdsProp.slice(0, maxItemCount), [itemIdsProp, maxItemCount]);
    const [itemsQueryRef, loadItemsQuery] = useQueryLoader<UserRecommendationsGridQuery>(
        userRecommendationsGridQuery
    );
    const [moreItemsQueryRef, loadMoreItemsQuery] = useQueryLoader<UserRecommendationsGridQuery>(
        userRecommendationsGridQuery
    );

    const initialItemIds = useMemo(
        () => itemIds.slice(0, initialItemCount),
        [itemIds, initialItemCount]
    );

    const moreItemIds = useMemo(() => itemIds.slice(initialItemCount), [itemIds, initialItemCount]);

    useEffect(() => {
        if (!initialItemIds.length || itemsQueryRef) {
            return;
        }
        loadItemsQuery({
            itemIds: [...initialItemIds],
            userId,
            isTrade,
            priceBookName,
        });
    }, [initialItemIds, isTrade, itemsQueryRef, loadItemsQuery, priceBookName, userId]);

    const itemsPerRow = getUserRecommendationsItemsPerRow(isMobile, isTablet);
    const hasMoreToLoad = itemIds.length > initialItemCount;

    const handleQueryLoadMore = useCallback(() => {
        if (!moreItemIds.length || moreItemsQueryRef) {
            return;
        }
        loadMoreItemsQuery({
            itemIds: [...moreItemIds],
            userId,
            isTrade,
            priceBookName,
        });
    }, [isTrade, loadMoreItemsQuery, moreItemIds, moreItemsQueryRef, priceBookName, userId]);

    const {
        isExpanded,
        moduleRef,
        showViewLess,
        showToggleButton,
        handleLoadMore,
        handleToggleExpand,
    } = useUserRecommendationsGridExpand({
        trackingTrigger,
        autoLoadMore,
        hasMoreToLoad,
        onLoadMore: handleQueryLoadMore,
    });

    if (!itemIds.length) {
        return null;
    }

    if (!itemsQueryRef) {
        return (
            <UserRecommendationsGridLayout
                isMobile={isMobile}
                isTablet={isTablet}
                dataTn={dataTn}
                title={title}
                titleAlign={titleAlign}
                moduleRef={moduleRef}
                isLoading
                showToggleButton={false}
                showViewLess={false}
                autoLoadMore={autoLoadMore}
                hasMoreToLoad={hasMoreToLoad}
                isExpanded={isExpanded}
                onToggleExpand={handleToggleExpand}
                onLoadMore={handleLoadMore}
            >
                {renderUserRecommendationsGridShimmers(initialItemCount, 'initial')}
            </UserRecommendationsGridLayout>
        );
    }

    return (
        <UserRecommendationsGridLayout
            isMobile={isMobile}
            isTablet={isTablet}
            dataTn={dataTn}
            title={title}
            titleAlign={titleAlign}
            moduleRef={moduleRef}
            showToggleButton={showToggleButton}
            showViewLess={showViewLess}
            autoLoadMore={autoLoadMore}
            hasMoreToLoad={hasMoreToLoad}
            isExpanded={isExpanded}
            onToggleExpand={handleToggleExpand}
            onLoadMore={handleLoadMore}
        >
            <Suspense
                fallback={renderUserRecommendationsGridShimmers(
                    initialItemCount,
                    'initial-loading'
                )}
            >
                <UserRecommendationsLoadedRows
                    queryRef={itemsQueryRef}
                    itemsPerRow={itemsPerRow}
                    isMobile={isMobile}
                    trackingTrigger={trackingTrigger}
                    dataTn={dataTn}
                    startIndex={0}
                    currency={currency}
                />
            </Suspense>
            {isExpanded && moreItemIds.length > 0 && !moreItemsQueryRef
                ? renderUserRecommendationsGridShimmers(moreItemCount, 'more')
                : null}
            {isExpanded && moreItemsQueryRef ? (
                <Suspense
                    fallback={renderUserRecommendationsGridShimmers(moreItemCount, 'more-loading')}
                >
                    <UserRecommendationsLoadedRows
                        queryRef={moreItemsQueryRef}
                        itemsPerRow={itemsPerRow}
                        isMobile={isMobile}
                        trackingTrigger={trackingTrigger}
                        dataTn={dataTn}
                        startIndex={initialItemCount}
                        currency={currency}
                    />
                </Suspense>
            ) : null}
        </UserRecommendationsGridLayout>
    );
};

export default UserRecommendationsItemsGrid;
